import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, ShieldCheck, ShieldX, Inbox } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, unwrap, API_BASE, getToken } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useI18n } from "@/context/I18nContext";

const STATUS_BADGE = {
  pending_review: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function AdminIdentityChecks() {
  const { locale } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending_review");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/identity-checks?status=${statusFilter}`);
      setItems(unwrap(r)?.items || []);
    } catch { toast.error("Could not load identity checks"); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({ total: items.length }), [items]);

  return (
    <div className="space-y-5" data-testid="admin-identity-checks-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">Identity checks</h1>
          <p className="text-sm text-[#4B5563]">Review the recheck selfies cleaners submit every 60 days.</p>
        </div>
        <div className="flex gap-2">
          {["pending_review", "approved", "rejected", "all"].map((s) => (
            <Button
              key={s}
              variant={statusFilter === s ? "default" : "outline"}
              size="sm"
              className={`rounded-xl ${statusFilter === s ? "bg-[#0EA5A4] hover:bg-[#0F766E] text-white" : ""}`}
              onClick={() => setStatusFilter(s)}
              data-testid={`identity-filter-${s}`}
            >
              {s.replace("_", " ")}
            </Button>
          ))}
        </div>
      </div>

      <Card className="rounded-2xl border border-[#E5E7EB] overflow-hidden">
        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" /></div>
        ) : counts.total === 0 ? (
          <div className="grid place-items-center py-16 text-center text-[#9CA3AF]" data-testid="identity-empty">
            <Inbox className="w-7 h-7 mb-2" />
            No cleaners {statusFilter === "all" ? "" : `with status ${statusFilter.replace("_", " ")}`}.
          </div>
        ) : (
          <ul className="divide-y divide-[#F3F4F6]" data-testid="identity-list">
            {items.map((c) => (
              <li key={c.id} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`identity-row-${c.id}`}>
                <Avatar src={c.photo_url} name={c.full_name} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{c.full_name}</div>
                  <div className="text-xs text-[#9CA3AF] truncate">{c.email}</div>
                  <div className="text-xs text-[#4B5563] mt-1">
                    {c.recheck_submitted_at && <>Submitted {formatDateTime(c.recheck_submitted_at, locale)} · </>}
                    {c.last_verified_at && <>Last verified {formatDateTime(c.last_verified_at, locale)}</>}
                  </div>
                </div>
                <Badge className={`border ${STATUS_BADGE[c.recheck_status] || "bg-stone-50 text-stone-600 border-stone-200"}`}>
                  {c.recheck_status || "—"}
                </Badge>
                <Button size="sm" className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" onClick={() => setSelected(c)} data-testid={`identity-review-${c.id}`}>
                  Review
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {selected && (
        <ReviewModal cleaner={selected} onClose={() => setSelected(null)} onDone={() => { setSelected(null); load(); }} />
      )}
    </div>
  );
}

function Avatar({ src, name }) {
  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  if (src) return <img src={src} alt={name} className="w-12 h-12 rounded-full object-cover shrink-0" />;
  return <div className="w-12 h-12 rounded-full bg-[#0EA5A4]/15 text-[#0F766E] grid place-items-center font-bold shrink-0">{initials}</div>;
}

function ReviewModal({ cleaner, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    if (!window.confirm(`Approve recheck for ${cleaner.full_name}? Their account stays active.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/identity-checks/${cleaner.id}/approve`, { reviewer_notes: notes });
      toast.success("Recheck approved");
      onDone();
    } catch { toast.error("Could not approve"); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    if (!window.confirm(`Reject recheck for ${cleaner.full_name}? Account will be paused.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/identity-checks/${cleaner.id}/reject`, { reviewer_notes: notes });
      toast.success("Recheck rejected — cleaner paused");
      onDone();
    } catch { toast.error("Could not reject"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="identity-review-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tighter">{cleaner.full_name}</DialogTitle>
          <DialogDescription>Compare the original onboarding selfie with the new recheck selfie.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <AuthPhotoTile label="Onboarding selfie" endpoint={`/admin/cleaners/${cleaner.id}/onboarding-selfie`} testid="onboarding-selfie" />
          <AuthPhotoTile label="New recheck selfie" endpoint={`/admin/cleaners/${cleaner.id}/recheck-selfie`} testid="recheck-selfie" />
        </div>
        <div className="mt-4">
          <label className="text-xs text-[#4B5563]">Reviewer notes (optional, sent on rejection)</label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-xl mt-1" data-testid="identity-reviewer-notes" />
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" className="rounded-xl" onClick={onClose}>Cancel</Button>
          <Button onClick={reject} disabled={busy} variant="outline" className="rounded-xl text-rose-600 border-rose-300 hover:bg-rose-50 hover:text-rose-700 font-semibold" data-testid="identity-reject-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldX className="w-4 h-4 mr-2" /> Reject</>}
          </Button>
          <Button onClick={approve} disabled={busy} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="identity-approve-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-2" /> Approve</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AuthPhotoTile({ label, endpoint, testid }) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    let blobUrl = "";
    (async () => {
      try {
        const r = await fetch(`${API_BASE}${endpoint}`, { headers: { Authorization: `Bearer ${getToken()}` } });
        if (!r.ok) throw new Error("fetch failed");
        const blob = await r.blob();
        blobUrl = URL.createObjectURL(blob);
        if (active) setUrl(blobUrl);
      } catch { if (active) setError(true); }
    })();
    return () => { active = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [endpoint]);
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold">{label}</div>
      <div className="aspect-square rounded-lg overflow-hidden bg-[#F5F5F4] border border-[#E5E7EB] grid place-items-center">
        {error ? (
          <div className="text-xs text-rose-500">Not available</div>
        ) : url ? (
          <img src={url} alt={label} className="w-full h-full object-cover" />
        ) : (
          <Loader2 className="w-4 h-4 animate-spin text-[#9CA3AF]" />
        )}
      </div>
    </div>
  );
}
